import { Loader2Icon, ServerCrashIcon } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import { ErrorPage } from '@/modules/errors/components/ErrorPage';
import { supportMailto } from '@/modules/support/support';
import { Button } from '@/modules/ui/components/button/Button';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  loadingFallback?: React.ReactNode;
}

export const ProtectedRoute = ({
  children,
  loadingFallback,
}: ProtectedRouteProps) => {
  const location = useLocation();
  const currentUserQuery = useCurrentUserQuery();
  const currentTenantQuery = useCurrentTenantQuery();

  const isLoading = currentUserQuery.isLoading || currentTenantQuery.isLoading;

  if (isLoading) {
    if (loadingFallback) return loadingFallback;

    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center"
        style={{ padding: '3.75rem 5rem' }}
      >
        <Loader2Icon className="animate-spin" />
        <Paragraph className="mt-2">Loading...</Paragraph>
      </div>
    );
  }

  // Only an authentication failure means the session is gone. A failed
  // company load (the tenant query below) is a data problem, not a signed-out
  // user, so it must not bounce the user to /login.
  const isUnauthenticated =
    currentUserQuery.isError ||
    (currentUserQuery.isSuccess && !currentUserQuery.data);

  if (isUnauthenticated) {
    const currentPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(currentPath)}`}
        replace
      />
    );
  }

  // The signed-in user's company failed to load. Every consumer of
  // ProtectedRoute (the HM portal, the signup flow, company intake,
  // permission-denied) reads the tenant through this same query, so this is
  // the one place that owns the failure instead of each page guessing why
  // its own data looks empty.
  if (currentTenantQuery.isError) {
    return (
      <ErrorPage
        icon={<ServerCrashIcon className="text-muted-foreground h-16 w-16" />}
        title="We couldn't load your company"
        message="Something went wrong loading your company's details. Try again, or contact support if this keeps happening."
        actions={
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              loading={currentTenantQuery.isFetching}
              onClick={() => void currentTenantQuery.refetch()}
            >
              Try again
            </Button>
            <Button asChild variant="outline">
              <a
                href={supportMailto('Company could not be loaded')}
                rel="noopener noreferrer"
                target="_blank"
              >
                Email support
              </a>
            </Button>
          </div>
        }
      />
    );
  }

  return children ? <>{children}</> : <Outlet />;
};
